import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { Send, Users, Search } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const location = useLocation();
  const [members, setMembers] = useState([]);
  const [active, setActive] = useState({ type: "community", name: "Community Room" });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/members").then((r) => setMembers(r.data.filter((m) => m.id !== user?.id))).catch(() => {});
  }, [user]);

  // open a DM if navigated with state.to
  useEffect(() => {
    const to = location.state?.to;
    if (to) setActive({ type: "dm", id: to.id, name: to.name, avatar: to.avatar });
  }, [location.state]);

  const load = useCallback(async () => {
    try {
      if (active.type === "community") {
        const { data } = await api.get("/chat/community");
        setMessages(data);
      } else {
        const { data } = await api.get(`/chat/dm/${active.id}`);
        setMessages(data.messages);
      }
    } catch { /* ignore */ }
  }, [active]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const body = input.trim();
    if (!body) return;
    setInput("");
    const optimistic = { id: `tmp_${Date.now()}`, sender_id: user.id, sender_name: user.name, sender_avatar: user.avatar, body, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      if (active.type === "community") await api.post("/chat/community", { body });
      else await api.post(`/chat/dm/${active.id}`, { body });
      load();
    } catch { /* ignore */ }
  };

  const filtered = members.filter((m) => m.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-bone pt-32 pb-12">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Stay Connected</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">Messages</h1>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-charcoal/10 border border-charcoal/10 mt-12 h-[calc(100vh-320px)] min-h-[520px]">
          {/* Sidebar */}
          <aside className="md:col-span-4 lg:col-span-3 bg-bone overflow-y-auto" data-testid="chat-sidebar">
            <button onClick={() => setActive({ type: "community", name: "Community Room" })} data-testid="channel-community"
              className={`w-full text-left flex items-center gap-3 p-5 border-b border-charcoal/10 transition-colors ${active.type === "community" ? "bg-navy text-bone" : "hover:bg-sand"}`}>
              <span className={`w-10 h-10 flex items-center justify-center ${active.type === "community" ? "bg-bone/15" : "bg-navy text-bone"}`}><Users size={18} strokeWidth={1.5} /></span>
              <div>
                <span className="font-serif text-lg leading-tight block">Community Room</span>
                <span className={`font-mono text-[10px] uppercase tracking-widest ${active.type === "community" ? "text-bone/70" : "text-charcoal/50"}`}>Everyone</span>
              </div>
            </button>

            <div className="p-4 border-b border-charcoal/10">
              <div className="flex items-center gap-2 border-b border-charcoal/20 pb-2">
                <Search size={14} className="text-charcoal/40" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a brother..." data-testid="dm-search"
                  className="w-full bg-transparent font-sans text-sm focus:outline-none" />
              </div>
            </div>

            <div>
              {filtered.map((m) => (
                <button key={m.id} onClick={() => setActive({ type: "dm", id: m.id, name: m.name, avatar: m.avatar })}
                  data-testid={`dm-${m.id}`}
                  className={`w-full text-left flex items-center gap-3 p-4 border-b border-charcoal/10 transition-colors ${active.type === "dm" && active.id === m.id ? "bg-navy text-bone" : "hover:bg-sand"}`}>
                  {m.avatar ? <img src={m.avatar} alt="" className="w-9 h-9 object-cover" /> : <span className="w-9 h-9 bg-gold text-charcoal flex items-center justify-center font-serif">{m.name[0]}</span>}
                  <span className="font-sans text-sm">{m.name}</span>
                </button>
              ))}
              {filtered.length === 0 && <p className="p-4 font-sans text-sm text-charcoal/40">No members found.</p>}
            </div>
          </aside>

          {/* Chat panel */}
          <section className="md:col-span-8 lg:col-span-9 bg-sand flex flex-col" data-testid="chat-panel">
            <header className="flex items-center gap-3 p-5 border-b border-charcoal/10 bg-bone">
              {active.type === "community" ? (
                <span className="w-10 h-10 bg-navy text-bone flex items-center justify-center"><Users size={18} strokeWidth={1.5} /></span>
              ) : active.avatar ? (
                <img src={active.avatar} alt="" className="w-10 h-10 object-cover" />
              ) : (
                <span className="w-10 h-10 bg-gold text-charcoal flex items-center justify-center font-serif text-lg">{active.name?.[0]}</span>
              )}
              <div>
                <h3 className="font-serif text-xl tracking-tight leading-none">{active.name}</h3>
                <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50">{active.type === "community" ? "Open discussion" : "Direct message"}</span>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-thread">
              {messages.map((m) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                      {!mine && active.type === "community" && <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50 mb-1">{m.sender_name}</span>}
                      <div className={`px-4 py-3 ${mine ? "bg-navy text-bone" : "bg-bone text-charcoal border border-charcoal/10"}`}>
                        <p className="font-sans leading-relaxed whitespace-pre-line">{m.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {messages.length === 0 && <p className="font-sans text-charcoal/40 text-center mt-10">No messages yet. Start the conversation.</p>}
              <div ref={endRef} />
            </div>

            <form onSubmit={send} className="flex gap-3 p-4 border-t border-charcoal/10 bg-bone" data-testid="chat-send-form">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Write a message..." data-testid="chat-message-input"
                className="flex-1 bg-transparent border-b border-charcoal/30 py-2 font-sans focus:outline-none focus:border-navy transition-colors" />
              <button type="submit" data-testid="chat-message-send" className="bg-charcoal text-bone px-5 hover:bg-navy transition-colors flex items-center"><Send size={16} strokeWidth={1.5} /></button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
